import { useEffect, useState } from "react";
import moment from "moment";
import { fetchLiveExchangeRate, findAllExchangeRates } from "../api/currency";
import { fetchAllPages } from "../api/pagination";
import { rateEffectiveDateKey } from "../rateEffectiveDate";

const DATE_KEY = "YYYY-MM-DD";

/**
 * Resuelve la tasa de cambio VIGENTE en una fecha, para mostrar montos
 * históricos convertidos con la tasa de ese día en vez de la del día de hoy.
 *
 * No confundir con useExchangeRateForDate, que es el de los formularios: ése
 * registra la tasa en la base cuando falta (porque al guardar un gasto hay que
 * persistir el monto convertido) y, si no la encuentra, cae en la ÚLTIMA tasa
 * registrada, que puede ser posterior a la fecha pedida. Para un reporte eso
 * es justo lo que no se quiere, así que acá:
 *
 *   1. Se usa la tasa registrada para esa fecha exacta.
 *   2. Si no hay, se consulta la tasa oficial histórica de ese día (solo
 *      lectura: a diferencia del hook de formularios, no registra nada).
 *   3. Si tampoco hay, se usa la última tasa registrada ANTERIOR a esa fecha
 *      (la que estaba vigente ese día) y se informa con isExactDate = false.
 *   4. Como último recurso, la tasa activa que reciba en fallbackRate.
 *
 * @param {object} params
 * @param {number|string|null} params.currencyId - moneda cuyas tasas se consultan.
 * @param {import('moment').Moment|string|null} params.date - fecha seleccionada.
 * @param {number|string|null} params.condominiumId - condominio, para la tasa oficial.
 * @param {number|null} params.fallbackRate - tasa activa, último recurso.
 */

const effectiveDateKey = (rate) =>
  moment(rate?.date || rate?.created_at).format(DATE_KEY);

// Ordena de más reciente a más antigua. Entre dos tasas del mismo día gana la
// vigente (el endpoint devuelve también las borradas) y, a igualdad, la última
// que se registró.
const byRecency = (a, b) => {
  const byDate = effectiveDateKey(b).localeCompare(effectiveDateKey(a));
  if (byDate !== 0) return byDate;

  const byActive = Number(b?.active !== false) - Number(a?.active !== false);
  if (byActive !== 0) return byActive;

  return moment(b?.created_at).valueOf() - moment(a?.created_at).valueOf();
};

const resolveFromRegistered = (rates, targetKey) => {
  const usable = (rates || []).filter((rate) => Number(rate?.value || 0) > 0);

  const sameDay = usable.filter((rate) => effectiveDateKey(rate) === targetKey);
  if (sameDay.length > 0) {
    const rate = [...sameDay].sort(byRecency)[0];
    return {
      value: Number(rate.value),
      dateKey: targetKey,
      isExactDate: true,
    };
  }

  const earlier = usable.filter((rate) => effectiveDateKey(rate) < targetKey);
  if (earlier.length > 0) {
    const rate = [...earlier].sort(byRecency)[0];
    return {
      value: Number(rate.value),
      dateKey: effectiveDateKey(rate),
      isExactDate: false,
    };
  }

  return null;
};

const useExchangeRateOnDate = ({
  currencyId,
  date,
  condominiumId,
  fallbackRate,
}) => {
  const targetKey = date ? moment(date).format(DATE_KEY) : null;
  const numericFallback = Number(fallbackRate || 0);

  const [resolution, setResolution] = useState({
    value: numericFallback,
    dateKey: null,
    isExactDate: true,
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!currencyId || !targetKey || !moment(targetKey, DATE_KEY, true).isValid()) {
      setResolution({
        value: numericFallback,
        dateKey: null,
        isExactDate: true,
      });
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const loadRate = async () => {
      setIsLoading(true);
      try {
        const response = await fetchAllPages((page, pageSize) => findAllExchangeRates(page, pageSize, {
          currency: currencyId,
        }));
        // HO-TASK-292: la tasa que aplica a un sábado o un domingo es la
        // del lunes siguiente, así que es esa la que se busca. Si el lunes
        // todavía no tiene tasa, la caída hacia atrás sigue igual.
        const appliedKey = rateEffectiveDateKey(targetKey) || targetKey;
        const registered = resolveFromRegistered(response?.data, appliedKey);

        if (registered?.isExactDate) {
          if (isMounted) setResolution(registered);
          return;
        }

        // Sin tasa registrada ese día: se pide la oficial de esa fecha antes
        // de caer en una tasa anterior, que es menos fiel al día pedido.
        let officialValue = null;
        let officialDateKey = appliedKey;
        try {
          const officialRate = await fetchLiveExchangeRate({
            condominium_id: condominiumId,
            date: targetKey,
          });
          if (Number(officialRate?.value || 0) > 0) {
            officialValue = Number(officialRate.value);
            officialDateKey = officialRate.effective_date || appliedKey;
          }
        } catch (officialError) {
          console.warn(
            "No se pudo obtener la tasa oficial para la fecha:",
            officialError
          );
        }

        if (!isMounted) return;

        if (officialValue) {
          setResolution({
            value: officialValue,
            dateKey: officialDateKey,
            isExactDate: true,
          });
          return;
        }

        setResolution(
          registered || {
            value: numericFallback,
            dateKey: null,
            isExactDate: false,
          }
        );
      } catch (error) {
        if (isMounted) {
          setResolution({
            value: numericFallback,
            dateKey: null,
            isExactDate: false,
          });
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadRate();

    return () => {
      isMounted = false;
    };
  }, [currencyId, targetKey, condominiumId, numericFallback]);

  return {
    // Tasa a aplicar. 0 cuando no se pudo resolver ninguna.
    rateValue: Number(resolution.value || 0),
    // Fecha de la tasa que se está aplicando (puede no ser la seleccionada).
    rateDateKey: resolution.dateKey,
    // false cuando se cayó en una tasa de otra fecha: hay que avisarlo.
    isExactDate: resolution.isExactDate,
    isRateLoading: isLoading,
  };
};

export default useExchangeRateOnDate;
